import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { Icon } from '@frontend/icons';
import { formatPhoneNumber } from '@frontend/phone-numbers';
import { usePreferredOutboundNumberUserSettings } from '@frontend/use-preferred-outbound-number-user-settings';
import { theme } from '@frontend/theme';
import { Chip, NakedButton, Text, Truncated, useTooltip } from '@frontend/design-system';

const DefaultSmsChip = () => {
  const { t } = useTranslation('inbox');
  return (
    <Chip
      variant='primary'
      tooltipLabel={t('Default SMS Number')}
      css={{ padding: theme.spacing(0.25), height: 16, width: 16 }}
    >
      <Icon name='location-default' height={12} width={12} />
    </Chip>
  );
};

const OutboundDisabledBadge = () => {
  const { t } = useTranslation('inbox');
  const { Tooltip, tooltipProps, triggerProps } = useTooltip();

  return (
    <>
      <span {...triggerProps} css={outboundDisabledBadgeStyles}>
        <Icon name='alert-invert' size={16} color='warn' />
      </span>
      <Tooltip {...tooltipProps}>{t('Outbound SMS not enabled for this number')}</Tooltip>
    </>
  );
};

const outboundDisabledBadgeStyles = css({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
});

const PreferredStars = ({
  isOutboundEnabled,
  locationId,
  phoneNumber,
}: {
  isOutboundEnabled: boolean;
  locationId: string;
  phoneNumber: string;
}) => {
  const { t } = useTranslation('inbox');
  const { Tooltip, tooltipProps, triggerProps, setOpen } = useTooltip();
  const {
    getPreferredOutboundNumberForLocation,
    setPreferredOutboundNumberForLocation,
    clearPreferredOutboundNumberForLocation,
  } = usePreferredOutboundNumberUserSettings();
  const preferredNumber = getPreferredOutboundNumberForLocation(locationId);
  const isPreferredNumber = preferredNumber === phoneNumber;

  // Don't show star for non-enabled phones
  if (!isOutboundEnabled) {
    return null;
  }

  return (
    <>
      {isPreferredNumber && (
        <NakedButton
          {...triggerProps}
          onClick={(e) => {
            e.stopPropagation();
            clearPreferredOutboundNumberForLocation(locationId);
            setOpen(false);
          }}
          trackingId='deselect-preferred-outbound-number-button'
        >
          <Icon name='star-filled' css={{ color: theme.colors.warning50 }} />
        </NakedButton>
      )}
      {!isPreferredNumber && (
        <NakedButton
          {...triggerProps}
          onClick={(e) => {
            e.stopPropagation();
            setPreferredOutboundNumberForLocation(locationId, phoneNumber);
            // close the tooltip always since it most likely will be opened when hovering over this star icon to click it
            setOpen(false);
          }}
          trackingId='select-preferred-outbound-number-button'
        >
          <Icon
            className='deselected-star-icon'
            name='star'
            css={{ color: theme.colors.neutral50, visibility: 'hidden' }}
          />
        </NakedButton>
      )}
      <Tooltip {...tooltipProps}>
        {isPreferredNumber ? t('Remove as preferred SMS number') : t('Make my preferred SMS number')}
      </Tooltip>
    </>
  );
};

type PhoneOptionProps = {
  defaultSms?: boolean;
  displayName?: string;
  isActive: boolean;
  isOutboundEnabled?: boolean;
  locationId: string;
  phoneNumber: string;
};

export const PhoneOption = ({
  defaultSms,
  displayName,
  isActive,
  isOutboundEnabled = true,
  locationId,
  phoneNumber,
}: PhoneOptionProps) => {
  const formattedPhone = formatPhoneNumber(phoneNumber, true);
  const showOutboundDisabledBadge = !isOutboundEnabled;

  if (displayName) {
    return (
      <div css={phoneOptionContainerStyles}>
        <div css={{ display: 'block', width: '100%', minWidth: 0 }}>
          <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Truncated
              css={{
                fontWeight: isActive ? 'bold' : 'normal',
                textAlign: 'left',
                maxWidth: `calc(100% - ${theme.spacing(3)})`,
                width: 'fit-content',
              }}
              as={Text}
            >
              {displayName}
            </Truncated>
            {defaultSms && <DefaultSmsChip />}
            {showOutboundDisabledBadge && <OutboundDisabledBadge />}
          </div>
          <Text color='subdued' size='small' textAlign='left'>
            {formattedPhone}
          </Text>
        </div>
        <PreferredStars isOutboundEnabled={isOutboundEnabled} locationId={locationId} phoneNumber={phoneNumber} />
      </div>
    );
  }

  return (
    <div css={phoneOptionContainerStyles}>
      <div css={{ display: 'block', width: '100%', minWidth: 0 }}>
        <div css={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <Text>{formattedPhone}</Text>
          {defaultSms && <DefaultSmsChip />}
          {showOutboundDisabledBadge && <OutboundDisabledBadge />}
        </div>
      </div>
      <PreferredStars isOutboundEnabled={isOutboundEnabled} locationId={locationId} phoneNumber={phoneNumber} />
    </div>
  );
};

const phoneOptionContainerStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  width: '100%',
  ':hover': {
    '.deselected-star-icon': {
      visibility: 'visible',
    },
  },
});
